import { FC, useState } from 'react';

import {
  isAllLineItemsPriceValid,
  isCartHavingDiscountedProducts,
  LineItem,
  ShoppingCartProps,
  Styled,
} from './';
import { Button, IconButton } from 'components/core';
import { useRouter } from 'next/router';
import { useEmptyCart, useProceedToCheckout } from 'reactQuery/mutations';
import Image from 'next/legacy/image';
import { LoadingSpinner } from 'styles/sharedStyles';
import { useIsMutating } from 'react-query';
import { useI18n } from 'contexts/i18n';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import ReactImageTooltip from 'react-image-tooltip';

const ShoppingCart: FC<ShoppingCartProps> = ({
  cart,
  setProceedToCheckout,
  selectedClamshellTemplate,
  promotionList,
  setErr,
}) => {
  const router = useRouter();
  const i18n = useI18n();
  const isMutating = useIsMutating();
  const isCustomClamShellOrder = cart.isCustomClamShellOrder;
  const [isTCAccepted, setIsTCAccpeted] = useState(false);

  const emptyCartMutation = useEmptyCart();

  const emptyCart = () => {
    emptyCartMutation.mutate();
  };
  const [stockError, setStockError] = useState();
  const proceedToCheckoutMutation = useProceedToCheckout(setStockError);

  const redirectToCheckout = () => {
    proceedToCheckoutMutation.mutate(undefined, {
      onSuccess: (data, variables, context) => {
        setProceedToCheckout(true);
        setErr('');
      },
    });
  };

  const isAllLineItemsInStock = () => {
    return cart.lineItems.every(item => item.inStock === true);
  };

  const isLineItemsPriceChanged = () => {
    return !cart.lineItems.every(
      item => item.isOriginalProductPriceChanged === false
    );
  };

  const isRewardPointsRedeemed = parseInt(cart?.totalRedeemPoints) > 0;
  return (
    <>
      <Styled.ShoppingCartHead>
        <Styled.Title>{i18n['Shopping Cart']}</Styled.Title>
        {cart.lineItems.length > 0 && (
          <IconButton
            width={'150px'}
            size="md"
            onClick={emptyCart}
            showBorder={true}
          >
            <>
              {!emptyCartMutation.isLoading && (
                <Image
                  src="/images/icons/delete.svg"
                  alt="me"
                  width="24"
                  height="24"
                />
              )}
              {emptyCartMutation.isLoading && (
                <LoadingSpinner width="20px" height="20px" />
              )}
              <Styled.EmptyCartButtonLabel>
                {i18n['Empty Cart']}
              </Styled.EmptyCartButtonLabel>
            </>
          </IconButton>
        )}
      </Styled.ShoppingCartHead>
      {cart.lineItems.length === 0 && (
        <Styled.EmptyCartMessage>
          <p>
            {i18n['Your cart is currently empty!'] ||
              'Your cart is currently empty '}
          </p>
          <Button
            size="xl"
            onClick={() => {
              router.push('/');
            }}
          >
            {i18n['Return To Shop']}
          </Button>
        </Styled.EmptyCartMessage>
      )}
      {cart.lineItems.length > 0 && (
        <Styled.ShoppingCartContainer>
          <Styled.ShoppingCartSection
            isCustomClamShellOrder={isCustomClamShellOrder}
          >
            <Styled.LineItemHeader
              isRewardPointsAvailable={isRewardPointsRedeemed}
            >
              <Styled.LineItemHeaderTitle>
                {i18n['Product']}
              </Styled.LineItemHeaderTitle>
              <Styled.LineItemHeaderTitle></Styled.LineItemHeaderTitle>
              <Styled.LineItemHeaderTitle>
                {i18n['Unit Price']}
              </Styled.LineItemHeaderTitle>
              {isRewardPointsRedeemed && (
                <Styled.LineItemHeaderTitle>
                  {i18n['Points']}
                </Styled.LineItemHeaderTitle>
              )}
              <Styled.LineItemHeaderTitle>
                {i18n['Quantity']}
              </Styled.LineItemHeaderTitle>
              <Styled.LineItemHeaderTitle>
                {i18n['Total']}
              </Styled.LineItemHeaderTitle>
            </Styled.LineItemHeader>
            {isCustomClamShellOrder ? (
              <>
                <Styled.CustomClamShellSummary>
                  <Styled.SummaryItem highlighted>
                    <Styled.ClamshellPreviewThumbnail>
                      {selectedClamshellTemplate &&
                        selectedClamshellTemplate.previewUrl && (
                          <ReactImageTooltip
                            image={selectedClamshellTemplate.previewUrl}
                            effect="solid"
                          >
                            <Image
                              src={selectedClamshellTemplate.previewUrl}
                              alt="Prod Image"
                              layout="fill"
                              objectFit="cover"
                              placeholder="blur"
                              blurDataURL="data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg=="
                            />
                          </ReactImageTooltip>
                        )}
                    </Styled.ClamshellPreviewThumbnail>

                    {/* {cart.itemCount} cases */}
                  </Styled.SummaryItem>
                  <Styled.SummaryItem>
                    {selectedClamshellTemplate?.name}
                  </Styled.SummaryItem>
                  <Styled.SummaryItem>{cart.displayTotal}</Styled.SummaryItem>
                  <Styled.SummaryItem>{cart.itemCount}</Styled.SummaryItem>
                  <Styled.SummaryItem highlighted>
                    {cart.displayTotal}
                  </Styled.SummaryItem>
                </Styled.CustomClamShellSummary>
                <Styled.Divider></Styled.Divider>
              </>
            ) : null}

            {cart.lineItems.map(lineItem => {
              return (
                <LineItem
                  key={lineItem.id}
                  lineItem={lineItem}
                  isCustomClamShellOrder={isCustomClamShellOrder}
                  promotionList={promotionList}
                  isRewardPointsAvailable={isRewardPointsRedeemed}
                  stockError={stockError}
                  setStockError={setStockError}
                />
              );
            })}

            {isCustomClamShellOrder ? (
              <Styled.EditDesignButton>
                <Button
                  onClick={() => {
                    router.push('/customcase/selectcasequantity');
                  }}
                  variant="tertiary"
                >
                  {i18n['Edit Quantity']}
                </Button>
              </Styled.EditDesignButton>
            ) : null}
          </Styled.ShoppingCartSection>
          <Styled.CheckoutSection>
            <Styled.SubTotalTitle>
              {`${i18n['Subtotal']}(${i18n['items:']} ${cart.itemCount})`}
            </Styled.SubTotalTitle>
            {isMutating > 0 ? (
              <Styled.LoadingSpinnerWrapper>
                <LoadingSpinner />
              </Styled.LoadingSpinnerWrapper>
            ) : (
              // <Styled.SubTotalPriceContainer>
              //   {isCartHavingDiscountedProducts(cart) && (
              //     <Styled.SubTotalWithDiscount>
              //       {cart.displayLineItemsTotalWithDiscounts}
              //     </Styled.SubTotalWithDiscount>
              //   )}
              //   <Styled.SubTotal
              //     isBulkDiscountAvailable={isCartHavingDiscountedProducts(cart)}
              //   >
              //     {cart.displayLineItemsTotalWithoutDiscount}
              //   </Styled.SubTotal>
              // </Styled.SubTotalPriceContainer>
              <Styled.SubTotalPriceContainer>
                <Styled.SubTotalWithDiscount>
                  {cart.displayLineItemsTotalWithDiscounts}
                </Styled.SubTotalWithDiscount>
                {isCartHavingDiscountedProducts(cart) && (
                  <Styled.TotalSavings>
                    {i18n['Total Savings']}: {cart.lineItemSavings.slice(1)}
                  </Styled.TotalSavings>
                )}
              </Styled.SubTotalPriceContainer>
            )}
            {parseInt(cart.totalRedeemPoints) > 0 && (
              <>
                <Styled.SubTotalTitle>
                  {i18n['Total Points Redeemed']}
                </Styled.SubTotalTitle>
                {isMutating > 0 ? (
                  <Styled.LoadingSpinnerWrapper>
                    <LoadingSpinner />
                  </Styled.LoadingSpinnerWrapper>
                ) : (
                  <Styled.SubTotalWithDiscount>
                    {cart.totalRedeemPoints}
                  </Styled.SubTotalWithDiscount>
                )}
              </>
            )}

            {(!isAllLineItemsInStock() || isLineItemsPriceChanged()) && (
              <Styled.CheckoutAlertMessage>
                {
                  i18n[
                    'Note: One of the product in your cart is out of stock or the price has been changed. So please remove the item from the cart and add it back.'
                  ]
                }
              </Styled.CheckoutAlertMessage>
            )}
            {cart.isCustomClamShellOrder && (
              <Styled.TCWrapper>
                <input
                  type="checkbox"
                  checked={isTCAccepted}
                  onChange={e => {
                    setIsTCAccpeted(e.target.checked);
                  }}
                  id="checkbox_id"
                />
                <label htmlFor="checkbox_id">
                  {
                    i18n[
                      'Confirm you agree with design layout and items included during custom design process. This needs to be confirmed prior adding to an order.'
                    ]
                  }
                </label>
              </Styled.TCWrapper>
            )}
            <Button
              variant="primary"
              fullWidth={true}
              onClick={redirectToCheckout}
              disabled={
                !isAllLineItemsInStock() ||
                isLineItemsPriceChanged() ||
                !isAllLineItemsPriceValid(cart) ||
                isMutating > 0 ||
                (cart.isCustomClamShellOrder && !isTCAccepted)
              }
            >
              {i18n['Checkout']}
            </Button>
          </Styled.CheckoutSection>
        </Styled.ShoppingCartContainer>
      )}
    </>
  );
};

export default ShoppingCart;
